/**
 * GlobalSearchResults — server-side search results for the phone.
 *
 * The App Finder's search box only ever filtered module *names* held in the
 * local catalog. That made the 158-module list the only thing findable: typing
 * a courier ID, a person's name or a batch number returned "nothing matches"
 * even for a founder with access to all of it, because no request was made.
 *
 * This renders what /dashboard/global-search returns — records across 15
 * categories, plus actions. Actions come back first and, where they can be
 * completed in one call, run here rather than routing to a page. That matters
 * on a phone: most module pages still carry no mobile layout, so an action that
 * navigates is an action that lands you on a desktop table.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import api from '../../utils/api';
import RecordSheet from './RecordSheet';
import { T as DS } from '../../design-system/tokens';

// Categories the record sheet can render. Anything else still navigates to its
// module — better an honest jump than a sheet that opens empty.
const SHEETABLE = new Set([
  'Couriers', 'Tasks', 'Formulations', 'SKUs', 'Packaging', 'Production', 'People', 'Clients',
]);

const CARD = {
  background: DS.surface,
  border: `1px solid ${DS.border}`,
  borderRadius: DS.r.lg,
  overflow: 'hidden',
  marginBottom: 14,
};

const HEADER = {
  fontSize: DS.type.xs,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: DS.textMuted,
  fontWeight: 700,
  fontFamily: DS.font,
  padding: '2px 4px 6px',
};

// Actions that finish in a single call. Anything not listed here navigates to
// its link instead — better a page than a button that silently does nothing.
const INLINE_ACTIONS = {
  'clock-in':  { endpoint: '/attendance/clock-in',  done: 'Clocked in'  },
  'clock-out': { endpoint: '/attendance/clock-out', done: 'Clocked out' },
};

const Row = ({ title, subtitle, tone, last, busy, onClick }) => (
  <button
    onClick={onClick}
    disabled={busy}
    style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%',
      padding: '13px 15px', minHeight: 56, textAlign: 'left',
      background: 'none', border: 'none',
      borderBottom: last ? 'none' : `1px solid ${DS.divider}`,
      cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.55 : 1,
      fontFamily: DS.font, WebkitTapHighlightColor: 'transparent',
    }}
  >
    <span style={{
      width: 8, height: 8, borderRadius: 4, flexShrink: 0,
      background: tone === 'action' ? DS.success : DS.accent,
    }} />
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{
        display: 'block', fontSize: DS.type.md, fontWeight: 600,
        color: DS.text, lineHeight: 1.3,
      }}>
        {title}
      </span>
      {subtitle && (
        <span style={{
          display: 'block', fontSize: DS.type.sm, color: DS.textMuted, marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {subtitle}
        </span>
      )}
    </span>
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={DS.borderStrong}
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </button>
);

export default function GlobalSearchResults({ query, onNavigate, onEmptyChange }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [state, setState] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const [flash, setFlash] = useState('');
  const [record, setRecord] = useState(null);

  // Guards an out-of-order response overwriting a newer one: typing "cur" fires
  // three requests and the shortest is not reliably the first to return.
  const seq = useRef(0);

  useEffect(() => {
    const q = (query || '').trim();
    if (q.length < 2) {
      setGroups([]);
      setState('idle');
      return;
    }
    const mine = ++seq.current;
    setState('loading');
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/dashboard/global-search', { params: { q } });
        if (mine !== seq.current) return;
        setGroups(data?.results || []);
        setState('ready');
      } catch (e) {
        if (mine !== seq.current) return;
        // Say what failed. A silently empty list here is indistinguishable from
        // "no such record", which is the bug this whole screen exists to fix.
        setError(e?.response?.data?.error || e.message || 'Search failed');
        setGroups([]);
        setState('error');
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const total = groups.reduce((n, g) => n + (g.items?.length || 0), 0);

  useEffect(() => {
    onEmptyChange?.(state === 'ready' && total === 0);
  }, [state, total, onEmptyChange]);

  const runAction = useCallback(async (item) => {
    const inline = INLINE_ACTIONS[item.action];
    if (!inline) {
      onNavigate?.();
      navigate(item.link);
      return;
    }
    setBusyAction(item.action);
    try {
      await api.post(inline.endpoint);
      setFlash(inline.done);
      setTimeout(() => setFlash(''), 2200);
    } catch (e) {
      setFlash(e?.response?.data?.error || 'Could not record that');
      setTimeout(() => setFlash(''), 3200);
    } finally {
      setBusyAction(null);
    }
  }, [navigate, onNavigate]);

  const open = (group, item) => {
    if (group.category === 'Actions') return runAction(item);
    if (SHEETABLE.has(group.category)) {
      setRecord({ category: group.category, id: item.id, title: item.title, link: item.link });
      return;
    }
    onNavigate?.();
    navigate(item.link);
  };

  // The sheet renders outside this guard: clearing the query while a record is
  // open should not yank it off the screen mid-read.
  const sheet = (
    <RecordSheet
      record={record}
      onClose={() => setRecord(null)}
      onNavigate={onNavigate}
    />
  );

  if ((query || '').trim().length < 2) return sheet;

  return (
    <div>
      {sheet}
      {flash && (
        <div style={{
          ...CARD, padding: '11px 14px', marginBottom: 12,
          fontSize: DS.type.sm, color: DS.text, fontFamily: DS.font,
          borderColor: DS.accent,
        }}>
          {flash}
        </div>
      )}

      {state === 'loading' && groups.length === 0 && (
        <div style={{ ...HEADER, padding: '2px 4px 10px' }}>Searching…</div>
      )}

      {state === 'error' && (
        <div style={{
          ...CARD, padding: '13px 15px', fontSize: DS.type.sm,
          color: DS.danger, fontFamily: DS.font, borderColor: DS.danger,
        }}>
          {error}
        </div>
      )}

      {groups.map(group => (
        <div key={group.category}>
          <div style={HEADER}>
            {group.icon ? `${group.icon} ` : ''}{group.category}
          </div>
          <div style={CARD}>
            {group.items.map((item, i) => (
              <Row
                key={`${group.category}-${item.id}`}
                title={item.title}
                subtitle={item.subtitle}
                tone={group.category === 'Actions' ? 'action' : 'record'}
                last={i === group.items.length - 1}
                busy={busyAction === item.action}
                onClick={() => open(group, item)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
